import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';
import { Popover } from '@material-ui/core';
import IntlMessages from 'util/IntlMessages';
import { BASE_APP_PATH } from 'new-design/routes/common/constants';
import { PreviewPanel, Entry } from 'new-design/components/PreviewPanel';
import { Tag } from 'new-design/common/ui';
import * as releaseNotes from 'ducks/releaseNotes';
import * as auth from 'ducks/auth';
import * as settings from 'ducks/settings';
import { formatReleaseDate, formatVersion, splitTags } from '../helpers';
import { ReleaseNoteTag } from './ReleaseNoteTag';

const STORAGE_KEY = 'releaseNotesLastSeenID';
const ANCHOR_SELECTOR = '[data-menu-key="information"]';
const MAX_ITEMS = 3;
const PAPER_STYLE = { marginTop: 8, borderRadius: 12, width: 380 };

const ReleaseNotesPreview = () => {
  const history = useHistory();
  const { requestReleaseNotes } = bindActionCreators(releaseNotes.actions, useDispatch());
  const sessionID = useSelector(auth.getSessionID);
  const locale = useSelector((state) => settings.selectors(state)?.locale?.locale);
  const items = useSelector(releaseNotes.getReleaseNotes) || [];
  const [anchorEl, setAnchorEl] = useState(null);
  const [unseen, setUnseen] = useState([]);

  useEffect(() => {
    if (!sessionID) return;
    const lastSeenID = Number(localStorage.getItem(STORAGE_KEY) || 0);
    requestReleaseNotes({ lastSeenID });
  }, [sessionID, locale]);

  useEffect(() => {
    if (!sessionID || anchorEl || !items.length) return;
    const lastSeenID = Number(localStorage.getItem(STORAGE_KEY) || 0);
    const fresh = items.filter((i) => (i.releaseNoteID || 0) > lastSeenID);
    const target = fresh.length ? document.querySelector(ANCHOR_SELECTOR) : null;
    if (!target) return;
    const maxID = items.reduce((m, i) => Math.max(m, i.releaseNoteID || 0), 0);
    localStorage.setItem(STORAGE_KEY, String(maxID));
    setUnseen(fresh);
    setAnchorEl(target);
  }, [sessionID, anchorEl, items]);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleOpenAll = useCallback(() => {
    history.push(`${BASE_APP_PATH}/release-notes`);
    setAnchorEl(null);
  }, [history]);

  const limited = unseen.slice(0, MAX_ITEMS);
  const latestVersion = limited.find((i) => i.version)?.version;

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ style: PAPER_STYLE }}
    >
      <PreviewPanel
        title={<IntlMessages id="whats_new" />}
        subtitle={<IntlMessages id="product_news_updates_summary" values={{ count: limited.length }} />}
        badge={latestVersion && <Tag color="primary">{formatVersion(latestVersion)}</Tag>}
        footer={{ label: <IntlMessages id="product_news_open" />, onClick: handleOpenAll }}
        onClose={handleClose}
      >
        {limited.map((item) => (
          <Entry
            key={item.releaseNoteID}
            tags={splitTags(item.tags).map((tag) => (
              <ReleaseNoteTag key={tag} tag={tag} />
            ))}
            date={formatReleaseDate(item.publishedAt, locale, 'MMM D')}
            title={item.title}
          />
        ))}
      </PreviewPanel>
    </Popover>
  );
};

export default ReleaseNotesPreview;
